import { useEffect } from 'react';

import { RootComponentInstance } from '@uniformdev/canvas';
import { UniformEntryPage } from 'src/pages/uniform-entry/uniform-entry-page';
import { useWAEContext } from 'src/uniform/utils/wae-context-provider';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { ProductKeys } from '@atlassiansox/asc-stargate';

import { CrossFlowTouchpointProvider } from '@/components/cross-flow/touchpoint/context';
import { hasJiraImcCampaignKey } from '@/components/signup/utils/url';
import { useLoggedInHasJira, useLoggedOut } from '@/components/user-context/helpers/cohorts';
import { IntercomProvider } from '@/libraries/intercom/src/provider';
import { LoggedInHeroContainer } from '@/templates/software/jira/logged-in/organic-logged-in/logged-in-hero-container';
import { OrganicLoggedInJiraProductTourOverview } from '@/templates/software/jira/logged-in/organic-logged-in/organic-logged-in-jira-product-tour-overview.template';
import { useRecentJiraSite } from '@/templates/software/jira/logged-in/utils/use-recent-jira-site';
import { LoggedOutOrNoJiraProductTourOverview } from '@/templates/software/jira/logged-out/jira-software-logged-out-page/src/jira-software-logged-out-page.template';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment, EnrollmentType } from '@/utils/experiment/use-experiment';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { JiraProductTourAiTemplate } from '../../../../templates/paid-landing/src/cfind-6215-frictionless-first-look-jira-v2.template';

export const buildJiraForYouUrl = (siteUrl?: string): string | undefined => {
	if (!siteUrl) {
		return undefined;
	}

	try {
		return new URL('/jira/for-you?tab=recommendedActions', siteUrl).href;
	} catch {
		return undefined;
	}
};

export const JiraSoftwarePage = ({ utmCampaign }: { utmCampaign?: string }) => {
	const loggedInHasJira = useLoggedInHasJira();
	const isLoggedOut = useLoggedOut();
	const isWae = checkFeatureFlagGate(featureFlags.dxpMigrationJiraProductTourGate);

	// FYI, this is currently JA only, since we only have JA work space appId
	const isBootIntercom = checkFeatureFlagGate(featureFlags.gwp737EnableIntercomChatJsw);

	const hasImcKey = hasJiraImcCampaignKey(utmCampaign);

	// Jira FFl experiment check
	const jiraFflExperimentCheck = useExperiment(experiments.cfind6469FrictionlessFirstLookJiraV3);
	const isJiraFflVariant =
		jiraFflExperimentCheck.type === EnrollmentType.ENROLLED && jiraFflExperimentCheck.value;

	const redirectLoggedInUsersExperiment = useExperiment(experiments.d1d6redirectLoggedInUsers);
	const shouldRedirectLoggedInUsers =
		redirectLoggedInUsersExperiment.type === EnrollmentType.ENROLLED &&
		redirectLoggedInUsersExperiment.value;

	let template: React.ReactNode = <LoggedOutOrNoJiraProductTourOverview />;

	// For logged in, we will either redirect to Jira, show a "Has Jira" experience,
	// or otherwise the Uniform "logged out" page.
	if (loggedInHasJira) {
		template = shouldRedirectLoggedInUsers ? (
			<RedirectingLoggedInJiraPage />
		) : (
			<LoggedInHeroContainer>
				<OrganicLoggedInJiraProductTourOverview />
			</LoggedInHeroContainer>
		);
	} else {
		if (hasImcKey && isJiraFflVariant && isLoggedOut) {
			template = <JiraProductTourAiTemplate />;
		} else if (isWae) {
			template = <WaeJiraPage />;
		}
	}

	return (
		<UFOSegment name="jira-product-tour-overview">
			<IntercomProvider appId="p0ww63i2" triggerBoot={isBootIntercom}>
				<CrossFlowTouchpointProvider
					productKeys={[ProductKeys.JIRA_SOFTWARE]}
					screenName="wacJswProductTourPage"
				>
					{template}
				</CrossFlowTouchpointProvider>
			</IntercomProvider>
		</UFOSegment>
	);
};

const RedirectingLoggedInJiraPage = () => {
	const { siteUrl } = useRecentJiraSite();
	const jiraForYouUrl = buildJiraForYouUrl(siteUrl);

	useEffect(() => {
		if (jiraForYouUrl) {
			window.location.assign(jiraForYouUrl);
		}
	}, [jiraForYouUrl]);

	return jiraForYouUrl ? null : (
		<LoggedInHeroContainer>
			<OrganicLoggedInJiraProductTourOverview />
		</LoggedInHeroContainer>
	);
};

const WaeJiraPage = () => {
	const { composition } = useWAEContext();

	const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
		experiments.cfind6469FrictionlessFirstLookJiraV3,
	);

	useEffect(() => {
		if (!jiraFflExposureFired) {
			fireJiraFflExposure();
		}
	}, [jiraFflExposureFired, fireJiraFflExposure]);

	return <UniformEntryPage composition={composition as RootComponentInstance} />;
};
